import { ButtonWidget, DialogPositioningBehavior, DialogType, TabStyles } from '@tableau/widgets';
import React from 'react';

import { dialogBodyStyle } from '../../app/content/styles/ExternalContentActionsStyles';
import { Messages } from '../../codegen/Localize';
import { ModalInstance, ReactModalOptions, ReactModalService } from './ReactModalService';
import { TabDialogWidth } from './style/TabDialogWidth';

export interface ReactAlertDialogOptions {
  title: string;
  message: string;
  dialogWidth?: TabDialogWidth;
  buttonText?: string;
}

/**
 * Displays a dialog that contains a message for the user and a close button.
 *
 * Returns a ModalInstance that holds a Promise in `.result`.
 * - If the user clicks the close button or clicks on the dialog glass, the promise is resolved.
 * - The dialog is setup so the promise is never rejected, since this is a simple alert.
 *
 * See `ReactModalService.open` for more information.
 */
export function openReactAlertDialog(options: ReactAlertDialogOptions): ModalInstance<any> {
  let modalInstance: ModalInstance<any>;

  function closeDialog() {
    modalInstance.close();
  }

  const modalOptions: ReactModalOptions = {
    testId: ReactAlertDialogTestId,
    title: options.title,
    type: DialogType.LightweightTitleBar,
    showCloseButton: false,
    dismissDialogOnCloseAction: false,
    positioningBehavior: DialogPositioningBehavior.Centered,
    dialogWidth: options.dialogWidth ?? TabDialogWidth.width375px,
    dialogBodyStyle: dialogBodyStyle,
    disableAutoFocus: false,
    dialogContent: <ReactAlertDialog {...options} closeDialog={closeDialog} />
  };

  modalInstance = ReactModalService.open(modalOptions);
  return modalInstance;
}

type ReactAlertDialogProps = ReactAlertDialogOptions & {
  closeDialog: ModalInstance['close'];
}

function ReactAlertDialog(props: ReactAlertDialogProps): JSX.Element {

  return (
    <>
      <div data-tb-test-id={ReactAlertDialogBodyTestId}>
        {props.message}
      </div>
      <div style={buttonContainerStyle}>
        <ButtonWidget
          testId={ReactAlertDialogClosebuttonTestId}
          handleClick={props.closeDialog}
          children={props.buttonText ?? Messages.close()}
        />
      </div>
    </>
  );
}

const buttonContainerStyle: React.CSSProperties = {
  marginTop: TabStyles.Spacing.MediumSpace,
  display: 'flex',
  justifyContent: 'flex-end'
};

export const ReactAlertDialogTestId = 'react-alert-dialog';
export const ReactAlertDialogClosebuttonTestId = 'react-alert-dialog-close';
export const ReactAlertDialogBodyTestId = 'react-alert-dialog-message-body';
